import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import { readRangeContent } from "./excelService";
import {
  ID_LOOKUP_MAX_CALLS_PER_WAVE,
  executeIdLookupToolDetailed,
} from "./idLookupTool";
import {
  describeIdLookupUnavailable,
  resolveIdLookupAvailability,
} from "./idLookupMode";
import { executeModifyWorkbookTool } from "./executorWriteTools";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import type { IdLookupRequestedField } from "./idLookupRoutingPolicy";
import type { ToolCallDetailedResult } from "./toolRegistry";
import {
  FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
  REAUTH_REQUIRED_OAUTH_ERROR_CODES,
} from "../shared/factset/factsetConstants";
import {
  mapWithConcurrencyLimit,
  type ExecutorWorkloadParticipant,
  type IdLookupOutputScope,
} from "./executorWorkload";
import {
  ID_LOOKUP_MAX_NONBLANK_ROWS,
  requiredExecutorIterationsForIdLookup,
} from "./executorLimits";

export const START_ID_LOOKUP_WORKLOAD_TOOL_NAME = "start_id_lookup_workload";
export const NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME =
  "lookup_next_id_lookup_wave";
export const COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME =
  "commit_id_lookup_wave";
export const ID_LOOKUP_UNRESOLVED_MARKER = "Unable to verify";

const LOOKUP_CONCURRENCY = 4;
const SOURCE_READ_PAGE_ROWS = 20_000;
const MAX_CHILD_OBSERVATIONS = ID_LOOKUP_MAX_CALLS_PER_WAVE;
const MAX_CHILD_ROW_NUMBERS = ID_LOOKUP_MAX_CALLS_PER_WAVE;
const ID_LOOKUP_OUTPUT_FIELDS = [
  "ticker",
  "entity_id",
  "ultimate_parent_name",
  "ultimate_parent_id",
  "domicile_country",
] as const satisfies readonly IdLookupRequestedField[];
const ID_LOOKUP_OUTPUT_FIELD_SET = new Set<IdLookupRequestedField>(
  ID_LOOKUP_OUTPUT_FIELDS,
);
const UNRESOLVED_REASONS = [
  "no_match",
  "ambiguous",
  "requested_fields_unavailable",
  "lookup_failed",
] as const;
const UNRESOLVED_REASON_SET = new Set<string>(UNRESOLVED_REASONS);

export type IdLookupUnresolvedReason = (typeof UNRESOLVED_REASONS)[number];

class IdLookupReauthenticationRequiredError extends Error {
  constructor(readonly errorCode: string) {
    super(
      "FactSet authentication expired. Reconnect FactSet, then retry the pending lookup wave.",
    );
    this.name = "IdLookupReauthenticationRequiredError";
    Object.setPrototypeOf(
      this,
      IdLookupReauthenticationRequiredError.prototype,
    );
  }
}

const nullableStringSchema = {
  type: ["string", "null"],
} as const;

export const START_ID_LOOKUP_WORKLOAD_TOOL: ChartCapabilityToolDef = {
  name: START_ID_LOOKUP_WORKLOAD_TOOL_NAME,
  description:
    "Register the complete worksheet company-identity workload before resolving a list. " +
    "The source must be one company-name column. Declare exactly the identity fields " +
    "requested by the user in destination-column order; the aligned target width must " +
    "match that field count. Target rows that already hold a value or formula are " +
    "skipped, so a range left incomplete by an earlier run can be registered again to " +
    "fill only its still-blank rows; a range whose rows are all populated is rejected, " +
    "and the answer to that rejection is a different target range, never clearing data " +
    "that is already there or that other formulas read.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        ...nullableStringSchema,
        description:
          "Run step that owns the workbook writes, or null for a run-level fix.",
      },
      sheetName: {
        type: "string",
        description: "Worksheet containing both source and target ranges.",
      },
      sourceAddress: {
        type: "string",
        description:
          "Complete one-column company source range, excluding its header (for example B2:B567).",
      },
      targetAddress: {
        type: "string",
        description:
          "Complete aligned output range whose width equals outputFields (for example C2:D567).",
      },
      outputFields: {
        type: "array",
        minItems: 1,
        items: {
          type: "string",
          enum: [...ID_LOOKUP_OUTPUT_FIELDS],
        },
        description:
          "Requested identity fields in exact destination-column order. Do not include unrequested fields.",
      },
    },
    required: [
      "stepId",
      "sheetName",
      "sourceAddress",
      "targetAddress",
      "outputFields",
    ],
    additionalProperties: false,
  },
};

export const NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL: ChartCapabilityToolDef = {
  name: NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  description:
    "Resolve the next pending worksheet lookup wave. The runtime selects at most 20 " +
    "registered rows, deduplicates equivalent source text, and runs bounded FactSet " +
    "company lookups. Commit every returned row before requesting another wave.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      workloadId: {
        type: "string",
        description: "Workload ID returned by start_id_lookup_workload.",
      },
    },
    required: ["workloadId"],
    additionalProperties: false,
  },
};

export const COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL: ChartCapabilityToolDef = {
  name: COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  description:
    "Write and read back the active worksheet lookup wave. Supply exactly one flat " +
    "resolution per returned row. Select one stored candidate by zero-based index, " +
    "or choose one closed unresolved reason. The runtime derives all requested cells " +
    "from that one candidate and owns the unresolved marker.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      workloadId: {
        type: "string",
        description: "Workload ID returned by start_id_lookup_workload.",
      },
      resolutions: {
        type: "array",
        minItems: 1,
        maxItems: ID_LOOKUP_MAX_CALLS_PER_WAVE,
        items: {
          type: "object",
          properties: {
            rowNumber: {
              type: "integer",
              minimum: 1,
              description:
                "Worksheet row number returned by lookup_next_id_lookup_wave.",
            },
            candidateIndex: {
              type: ["integer", "null"],
              minimum: 0,
              description:
                "Zero-based candidate index from the row's stored lookup result, or null when unresolved.",
            },
            unresolvedReason: {
              type: ["string", "null"],
              enum: [...UNRESOLVED_REASONS, null],
              description:
                "Closed unresolved reason, or null when candidateIndex selects a candidate.",
            },
          },
          required: ["rowNumber", "candidateIndex", "unresolvedReason"],
          additionalProperties: false,
        },
      },
    },
    required: ["workloadId", "resolutions"],
    additionalProperties: false,
  },
};

export const ID_LOOKUP_WORKLOAD_TOOLS: readonly ChartCapabilityToolDef[] = [
  START_ID_LOOKUP_WORKLOAD_TOOL,
  NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL,
  COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL,
];

interface ParsedA1Range {
  startColumn: number;
  endColumn: number;
  startRow: number;
  endRow: number;
}

type LookupRowStatus = "pending" | "looked_up" | "committed" | "verified";

interface StoredCandidate {
  rank: number | null;
  id: string | null;
  id_type: string | null;
  dataset: string | null;
  company_type: string | null;
  name: string | null;
  matched_name: string | null;
  issuer_name: string | null;
  ticker: string | null;
  entity_id: string | null;
  security_type: string | null;
  country: string | null;
  website: string | null;
  ultimate_parent_entity_id: string | null;
  ultimate_parent_company_name: string | null;
}

interface StoredLookupResult {
  output: string;
  candidates: StoredCandidate[];
  failed: boolean;
  status: string;
  fallbackReason: string | null;
  errorCode: string | null;
  error: string | null;
  observationMetadata?: Record<string, unknown>;
}

interface LookupWorkloadRow {
  rowNumber: number;
  sourceCell: string;
  targetAddress: string;
  searchText: string;
  lookupKey: string;
  status: LookupRowStatus;
  candidateIndex?: number;
  unresolvedReason?: IdLookupUnresolvedReason;
}

interface StoredResolution {
  candidateIndex: number | null;
  unresolvedReason: IdLookupUnresolvedReason | null;
}

interface LookupWorkload {
  id: string;
  stepId: string | null;
  sheetName: string;
  sourceAddress: string;
  targetAddress: string;
  sourceRange: ParsedA1Range;
  targetRange: ParsedA1Range;
  outputFields: IdLookupRequestedField[];
  rows: LookupWorkloadRow[];
  activeWaveRows: number[];
  lookupCache: Map<string, StoredLookupResult>;
  resolutionByLookupKey: Map<string, StoredResolution>;
}

interface CommitDecision {
  rowNumber: number;
  candidateIndex: number | null;
  unresolvedReason: IdLookupUnresolvedReason | null;
}

interface ValidatedCommitResolution extends CommitDecision {
  lookupKey: string;
  values: string[];
}

export interface IdLookupRoutingRecord {
  args: {
    search_text: string;
    filters: null;
    limit: number;
  };
  output: string;
  rowNumbers: number[];
}

export interface IdLookupWorkloadCompletionIssue {
  issue:
    | "id_lookup_workload_pending"
    | "id_lookup_workload_wave_uncommitted";
  workloadId: string;
  nextTool:
    | typeof NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME
    | typeof COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME;
  remainingRows: number;
  nextRowNumbers: number[];
  instruction: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function parseA1Range(address: string): ParsedA1Range | null {
  const bareAddress = address.includes("!")
    ? address.slice(address.lastIndexOf("!") + 1)
    : address;
  const match = bareAddress
    .trim()
    .match(/^\$?([A-Z]+)\$?(\d+)(?::\$?([A-Z]+)\$?(\d+))?$/i);
  if (!match) return null;

  const toColumnNumber = (letters: string): number => {
    let value = 0;
    for (const character of letters.toUpperCase()) {
      value = value * 26 + character.charCodeAt(0) - 64;
    }
    return value;
  };
  const firstColumn = toColumnNumber(match[1]);
  const firstRow = Number(match[2]);
  const secondColumn = toColumnNumber(match[3] ?? match[1]);
  const secondRow = Number(match[4] ?? match[2]);
  if (
    !Number.isInteger(firstRow) ||
    !Number.isInteger(secondRow) ||
    firstRow < 1 ||
    secondRow < 1
  ) {
    return null;
  }
  return {
    startColumn: Math.min(firstColumn, secondColumn),
    endColumn: Math.max(firstColumn, secondColumn),
    startRow: Math.min(firstRow, secondRow),
    endRow: Math.max(firstRow, secondRow),
  };
}

function columnName(columnNumber: number): string {
  let current = columnNumber;
  let result = "";
  while (current > 0) {
    current -= 1;
    result = String.fromCharCode(65 + (current % 26)) + result;
    current = Math.floor(current / 26);
  }
  return result;
}

function rowRangeAddress(
  range: ParsedA1Range,
  startRow: number,
  endRow: number,
): string {
  return `${columnName(range.startColumn)}${startRow}:${columnName(
    range.endColumn,
  )}${endRow}`;
}

function readTrimmedString(
  record: Record<string, unknown>,
  field: string,
): string {
  return typeof record[field] === "string" ? record[field].trim() : "";
}

function readNullableString(
  record: Record<string, unknown>,
  field: string,
): string | null {
  const value = record[field];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizeLookupKey(value: string): string {
  return value
    .normalize("NFKC")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (typeof DOMException === "function") {
    throw new DOMException("Company lookup workload cancelled.", "AbortError");
  }
  const error = new Error("Company lookup workload cancelled.");
  error.name = "AbortError";
  throw error;
}

function parseModelOutput(output: string): unknown {
  try {
    return JSON.parse(output);
  } catch {
    return output;
  }
}

function parseOutputFields(rawValue: unknown): {
  outputFields?: IdLookupRequestedField[];
  error?: string;
} {
  if (!Array.isArray(rawValue) || rawValue.length === 0) {
    return {
      error: "outputFields must contain at least one requested identity field.",
    };
  }
  const outputFields: IdLookupRequestedField[] = [];
  for (let index = 0; index < rawValue.length; index += 1) {
    const field = rawValue[index];
    if (
      typeof field !== "string" ||
      !ID_LOOKUP_OUTPUT_FIELD_SET.has(field as IdLookupRequestedField)
    ) {
      return {
        error:
          `outputFields[${index}] must be one of: ` +
          `${ID_LOOKUP_OUTPUT_FIELDS.join(", ")}.`,
      };
    }
    const typedField = field as IdLookupRequestedField;
    if (outputFields.includes(typedField)) {
      return { error: `outputFields must not repeat ${typedField}.` };
    }
    outputFields.push(typedField);
  }
  return { outputFields };
}

function sameFieldSet(
  left: readonly IdLookupRequestedField[],
  right: readonly IdLookupRequestedField[],
): boolean {
  return (
    left.length === right.length &&
    left.every((field) => right.includes(field))
  );
}

function sameFieldOrder(
  left: readonly IdLookupRequestedField[],
  right: readonly IdLookupRequestedField[],
): boolean {
  return (
    left.length === right.length &&
    left.every((field, index) => field === right[index])
  );
}

function parseCommitDecisions(rawValue: unknown): {
  decisions?: CommitDecision[];
  error?: string;
} {
  if (!Array.isArray(rawValue) || rawValue.length === 0) {
    return {
      error: "resolutions must contain one entry for every active wave row.",
    };
  }

  const decisions: CommitDecision[] = [];
  const allowedKeys = new Set([
    "rowNumber",
    "candidateIndex",
    "unresolvedReason",
  ]);
  for (let index = 0; index < rawValue.length; index += 1) {
    const record = isRecord(rawValue[index]) ? rawValue[index] : null;
    if (!record) {
      return { error: `resolutions[${index}] must be an object.` };
    }
    const unknownKey = Object.keys(record).find(
      (key) => !allowedKeys.has(key),
    );
    if (unknownKey) {
      const legacy =
        unknownKey === "status" || unknownKey === "fieldValues"
          ? " Legacy status/fieldValues resolutions are not accepted."
          : "";
      return {
        error: `resolutions[${index}].${unknownKey} is not supported.${legacy}`,
      };
    }

    const rowNumber = record.rowNumber;
    if (!Number.isInteger(rowNumber) || (rowNumber as number) < 1) {
      return {
        error: `resolutions[${index}].rowNumber must be a positive integer.`,
      };
    }

    const candidateIndex = record.candidateIndex;
    if (
      candidateIndex !== null &&
      (!Number.isInteger(candidateIndex) || (candidateIndex as number) < 0)
    ) {
      return {
        error:
          `resolutions[${index}].candidateIndex must be a zero-based integer or null.`,
      };
    }

    const unresolvedReason = record.unresolvedReason;
    if (
      unresolvedReason !== null &&
      (typeof unresolvedReason !== "string" ||
        !UNRESOLVED_REASON_SET.has(unresolvedReason))
    ) {
      return {
        error:
          `resolutions[${index}].unresolvedReason must be one of ` +
          `${UNRESOLVED_REASONS.join(", ")}, or null.`,
      };
    }

    const hasCandidate = candidateIndex !== null;
    const hasUnresolvedReason = unresolvedReason !== null;
    if (hasCandidate === hasUnresolvedReason) {
      return {
        error:
          `resolutions[${index}] must select exactly one outcome: candidateIndex ` +
          "is non-null if and only if unresolvedReason is null.",
      };
    }

    decisions.push({
      rowNumber: rowNumber as number,
      candidateIndex: candidateIndex as number | null,
      unresolvedReason: unresolvedReason as IdLookupUnresolvedReason | null,
    });
  }
  return { decisions };
}

function normalizeCandidate(rawCandidate: Record<string, unknown>): StoredCandidate {
  return {
    rank:
      typeof rawCandidate.rank === "number" &&
      Number.isInteger(rawCandidate.rank)
        ? rawCandidate.rank
        : null,
    id: readNullableString(rawCandidate, "id"),
    id_type: readNullableString(rawCandidate, "id_type"),
    dataset: readNullableString(rawCandidate, "dataset"),
    company_type: readNullableString(rawCandidate, "company_type"),
    name: readNullableString(rawCandidate, "name"),
    matched_name: readNullableString(rawCandidate, "matched_name"),
    issuer_name: readNullableString(rawCandidate, "issuer_name"),
    ticker: readNullableString(rawCandidate, "ticker"),
    entity_id: readNullableString(rawCandidate, "entity_id"),
    security_type: readNullableString(rawCandidate, "security_type"),
    country: readNullableString(rawCandidate, "country"),
    website: readNullableString(rawCandidate, "website"),
    ultimate_parent_entity_id: readNullableString(
      rawCandidate,
      "ultimate_parent_entity_id",
    ),
    ultimate_parent_company_name: readNullableString(
      rawCandidate,
      "ultimate_parent_company_name",
    ),
  };
}

function parseStoredLookupResult(
  output: string,
  observationMetadata?: Record<string, unknown>,
): StoredLookupResult {
  const parsed = parseModelOutput(output);
  if (!isRecord(parsed)) {
    return {
      output,
      candidates: [],
      failed: true,
      status: "error",
      fallbackReason: "invalid_lookup_output",
      errorCode: null,
      error: "Company lookup returned invalid JSON.",
      observationMetadata,
    };
  }

  const rawCandidates = Array.isArray(parsed.candidates)
    ? parsed.candidates
    : [];
  const candidatesAreValid = rawCandidates.every(isRecord);
  const candidates = candidatesAreValid
    ? rawCandidates.map((candidate) => normalizeCandidate(candidate))
    : [];
  const status = readTrimmedString(parsed, "status");
  const failed =
    !candidatesAreValid || parsed.success === false || status === "error";
  return {
    output,
    candidates,
    failed,
    status: status || (failed ? "error" : candidates.length ? "ok" : "no_match"),
    fallbackReason: readNullableString(parsed, "fallback_reason"),
    errorCode: readNullableString(parsed, "error_code"),
    error: readNullableString(parsed, "error"),
    observationMetadata,
  };
}

/**
 * The run's FactSet sign-in ended, so no label can resolve until the user
 * reconnects. Every other lookup problem is a verdict about one label and
 * belongs on that row; this one is a property of the session, and reporting it
 * per row would close a whole wave of real companies as unmatched.
 */
export class TabularIdentityAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TabularIdentityAuthError";
    Object.setPrototypeOf(this, TabularIdentityAuthError.prototype);
  }
}

export type FactSetTabularIdentityResolution =
  | {
      status: "resolved";
      companyId?: string;
      companyTicker?: string;
    }
  | {
      status: "no_match" | "failed";
    };

/**
 * Deterministically resolves the top FactSet identity for the tabular adapter.
 *
 * The adapter owns its wave-level dedupe/cache; this helper deliberately
 * reuses the same authenticated PR #535 client and response parser as the
 * interactive ID workload instead of introducing another lookup transport.
 */
export async function resolveFactSetTabularIdentity(
  searchText: string,
  signal?: AbortSignal,
): Promise<FactSetTabularIdentityResolution> {
  throwIfAborted(signal);
  let detailed: ToolCallDetailedResult;
  try {
    detailed = await executeIdLookupToolDetailed(
      {
        search_text: searchText,
        filters: null,
        limit: FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
      },
      signal,
    );
  } catch {
    throwIfAborted(signal);
    return { status: "failed" };
  }
  throwIfAborted(signal);

  const result = parseStoredLookupResult(
    detailed.output,
    detailed.observationMetadata,
  );
  if (
    result.errorCode &&
    REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(result.errorCode as never)
  ) {
    throw new TabularIdentityAuthError(
      result.error ||
        "The FactSet connection for this session ended. Reconnect FactSet and run this again.",
    );
  }
  if (result.failed) return { status: "failed" };
  if (result.candidates.length === 0) return { status: "no_match" };

  const candidate = result.candidates.find(
    (value) => Boolean(value.id || value.entity_id || value.ticker),
  );
  if (!candidate) return { status: "failed" };
  return {
    status: "resolved",
    ...(candidate.id || candidate.entity_id
      ? { companyId: (candidate.id ?? candidate.entity_id) as string }
      : {}),
    ...(candidate.ticker ? { companyTicker: candidate.ticker } : {}),
  };
}

function candidateFieldValue(
  candidate: StoredCandidate,
  field: IdLookupRequestedField,
): string | null {
  switch (field) {
    case "ticker":
      return candidate.ticker;
    case "entity_id":
      return candidate.entity_id;
    case "ultimate_parent_name":
      return candidate.ultimate_parent_company_name;
    case "ultimate_parent_id":
      return candidate.ultimate_parent_entity_id;
    case "domicile_country":
      return candidate.country;
  }
}

function candidateHasAllFields(
  candidate: StoredCandidate,
  outputFields: readonly IdLookupRequestedField[],
): boolean {
  return outputFields.every(
    (field) => (candidateFieldValue(candidate, field)?.length ?? 0) > 0,
  );
}

function deriveResolutionValues(
  decision: CommitDecision,
  lookupResult: StoredLookupResult,
  outputFields: readonly IdLookupRequestedField[],
): { values?: string[]; error?: string } {
  if (decision.candidateIndex !== null) {
    if (lookupResult.failed) {
      return {
        error:
          `row ${decision.rowNumber} cannot select a candidate because its lookup failed.`,
      };
    }
    const candidate = lookupResult.candidates[decision.candidateIndex];
    if (!candidate) {
      return {
        error:
          `row ${decision.rowNumber} candidateIndex ${decision.candidateIndex} is out of range; ` +
          `the stored lookup has ${lookupResult.candidates.length} candidate(s).`,
      };
    }
    const missingFields = outputFields.filter(
      (field) => !candidateFieldValue(candidate, field),
    );
    if (missingFields.length > 0) {
      return {
        error:
          `row ${decision.rowNumber} candidateIndex ${decision.candidateIndex} is missing ` +
          `${missingFields.join(", ")}. Select one candidate containing every requested field ` +
          "or use requested_fields_unavailable.",
      };
    }
    return {
      values: outputFields.map(
        (field) => candidateFieldValue(candidate, field) as string,
      ),
    };
  }

  const reason = decision.unresolvedReason as IdLookupUnresolvedReason;
  const hasCompleteCandidate = lookupResult.candidates.some((candidate) =>
    candidateHasAllFields(candidate, outputFields),
  );
  let valid = false;
  switch (reason) {
    case "lookup_failed":
      valid = lookupResult.failed;
      break;
    case "no_match":
      valid = !lookupResult.failed && lookupResult.candidates.length === 0;
      break;
    case "ambiguous":
      valid = !lookupResult.failed && lookupResult.candidates.length > 0;
      break;
    case "requested_fields_unavailable":
      valid =
        !lookupResult.failed &&
        lookupResult.candidates.length > 0 &&
        !hasCompleteCandidate;
      break;
  }
  if (!valid) {
    return {
      error:
        `row ${decision.rowNumber} unresolvedReason ${reason} contradicts the stored ` +
        `lookup result (${lookupResult.candidates.length} candidate(s), status ` +
        `${lookupResult.status}).`,
    };
  }
  return { values: outputFields.map(() => ID_LOOKUP_UNRESOLVED_MARKER) };
}

function sameStoredResolution(
  left: StoredResolution,
  right: StoredResolution,
): boolean {
  return (
    left.candidateIndex === right.candidateIndex &&
    left.unresolvedReason === right.unresolvedReason
  );
}

function groupConsecutiveResolutions(
  resolutions: readonly ValidatedCommitResolution[],
): ValidatedCommitResolution[][] {
  const groups: ValidatedCommitResolution[][] = [];
  for (const resolution of resolutions) {
    const current = groups.at(-1);
    if (
      !current ||
      resolution.rowNumber !== current[current.length - 1].rowNumber + 1
    ) {
      groups.push([resolution]);
    } else {
      current.push(resolution);
    }
  }
  return groups;
}

function modelLookupView(result: StoredLookupResult): Record<string, unknown> {
  return {
    success: !result.failed,
    status: result.status,
    candidate_count: result.candidates.length,
    candidates: result.candidates.map((candidate, candidateIndex) => ({
      candidateIndex,
      ...candidate,
    })),
    fallback_reason: result.fallbackReason,
    error_code: result.errorCode,
    error: result.error,
  };
}

function sanitizeChildObservation(
  metadata: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!metadata) return {};
  const allowedKeys = [
    "lookupPath",
    "candidateCount",
    "publicCandidateCount",
    "privateCandidateCount",
    "parentEnrichmentStatus",
    "fallbackRecommended",
    "fallbackReason",
    "lookupRequestId",
    "retryCount",
    "upstreamDurationMs",
    "errorCode",
  ];
  return Object.fromEntries(
    allowedKeys
      .filter((key) => {
        const value = metadata[key];
        return (
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"
        );
      })
      .map((key) => [key, metadata[key]]),
  );
}

export class IdLookupWorkloadController
  implements ExecutorWorkloadParticipant<IdLookupWorkloadCompletionIssue>
{
  private workload: LookupWorkload | null = null;
  private workloadSequence = 0;
  private routingRecords: IdLookupRoutingRecord[] = [];
  private version = 0;
  private readonly authorizedOutputFields: IdLookupRequestedField[];
  private abortListenerAttached = false;
  private readonly handleRuntimeAbort = (): void => {
    this.reset();
  };

  constructor(private readonly ctx: ExecutorToolRuntimeContext) {
    this.authorizedOutputFields = Array.from(
      new Set(ctx.idLookupRequestedFields ?? []),
    );
  }

  get hasWorkload(): boolean {
    return this.workload !== null;
  }

  get progressVersion(): number {
    return this.version;
  }

  get activeWorkloadId(): string | null {
    return this.workload?.id ?? null;
  }

  get isComplete(): boolean {
    return Boolean(
      this.workload &&
        this.workload.activeWaveRows.length === 0 &&
        this.workload.rows.every((row) => row.status === "verified"),
    );
  }

  /** Releases this controller's reservation when its run is cancelled/reset. */
  reset(): void {
    const workload = this.workload;
    if (workload) {
      this.releaseOutputScope(workload.id);
      this.workload = null;
      this.routingRecords = [];
      this.version += 1;
    }
    this.detachAbortListener();
  }

  takeRoutingRecords(): IdLookupRoutingRecord[] {
    const records = this.routingRecords;
    this.routingRecords = [];
    return records;
  }

  completionIssue(): IdLookupWorkloadCompletionIssue | null {
    const workload = this.workload;
    if (!workload) return null;
    const remainingRows = workload.rows.filter(
      (row) => row.status !== "verified",
    );
    if (remainingRows.length === 0) return null;

    if (workload.activeWaveRows.length > 0) {
      return {
        issue: "id_lookup_workload_wave_uncommitted",
        workloadId: workload.id,
        nextTool: COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
        remainingRows: remainingRows.length,
        nextRowNumbers: [...workload.activeWaveRows],
        instruction:
          `The active worksheet lookup wave for rows ` +
          `${workload.activeWaveRows.join(", ")} is not fully written and verified. ` +
          `Call ${COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME} with exactly one flat ` +
          "candidateIndex/unresolvedReason resolution for every active row.",
      };
    }

    const pendingRows = remainingRows.filter((row) => row.status === "pending");
    return {
      issue: "id_lookup_workload_pending",
      workloadId: workload.id,
      nextTool: NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
      remainingRows: remainingRows.length,
      nextRowNumbers: pendingRows
        .slice(0, ID_LOOKUP_MAX_CALLS_PER_WAVE)
        .map((row) => row.rowNumber),
      instruction:
        `${remainingRows.length} registered company row(s) remain. Call ` +
        `${NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME} for workload ${workload.id}; ` +
        "do not finish or mark the step terminal while registered rows remain.",
    };
  }

  describeIncompleteRows(): string[] {
    const workload = this.workload;
    if (!workload) return [];
    return workload.rows
      .filter((row) => row.status !== "verified")
      .slice(0, 50)
      .map(
        (row) =>
          `${workload.sheetName}!${row.sourceCell} (${row.searchText}): ${row.status}`,
      );
  }

  async start(rawInput: unknown): Promise<string> {
    const availability = await resolveIdLookupAvailability();
    if (!availability.available) {
      return JSON.stringify({
        ok: false,
        error: describeIdLookupUnavailable(availability),
      });
    }

    const record = isRecord(rawInput) ? rawInput : {};
    const stepId =
      record.stepId === null
        ? null
        : typeof record.stepId === "string"
          ? record.stepId.trim()
          : "";
    const sheetName = readTrimmedString(record, "sheetName");
    const sourceAddress = readTrimmedString(record, "sourceAddress");
    const targetAddress = readTrimmedString(record, "targetAddress");
    const parsedOutputFields = parseOutputFields(record.outputFields);
    if (stepId === "") {
      return JSON.stringify({
        ok: false,
        error: "stepId must be a string or null.",
      });
    }
    if (!sheetName || !sourceAddress || !targetAddress) {
      return JSON.stringify({
        ok: false,
        error: "sheetName, sourceAddress, and targetAddress are required.",
      });
    }
    if (sourceAddress.includes("!") || targetAddress.includes("!")) {
      return JSON.stringify({
        ok: false,
        error:
          "sourceAddress and targetAddress must be unqualified A1 ranges. " +
          "Pass the worksheet separately in sheetName.",
      });
    }
    if (!parsedOutputFields.outputFields) {
      return JSON.stringify({ ok: false, error: parsedOutputFields.error });
    }
    const outputFields = parsedOutputFields.outputFields;
    if (!sameFieldSet(outputFields, this.authorizedOutputFields)) {
      return JSON.stringify({
        ok: false,
        error:
          "outputFields must exactly match the identity fields requested by the user. " +
          `Authorized fields: ${this.authorizedOutputFields.join(", ") || "none"}; ` +
          `received: ${outputFields.join(", ")}.`,
      });
    }
    if (
      stepId !== null &&
      !this.ctx.planState.steps.some((step) => step.id === stepId)
    ) {
      return JSON.stringify({
        ok: false,
        error: `Unknown stepId: "${stepId}".`,
      });
    }

    const sourceRange = parseA1Range(sourceAddress);
    const targetRange = parseA1Range(targetAddress);
    if (!sourceRange || !targetRange) {
      return JSON.stringify({
        ok: false,
        error: "sourceAddress and targetAddress must be finite A1 cell ranges.",
      });
    }
    if (sourceRange.startColumn !== sourceRange.endColumn) {
      return JSON.stringify({
        ok: false,
        error: "sourceAddress must contain exactly one column.",
      });
    }
    if (
      targetRange.endColumn - targetRange.startColumn + 1 !==
      outputFields.length
    ) {
      return JSON.stringify({
        ok: false,
        error:
          `targetAddress must contain exactly ${outputFields.length} column(s), one for each ` +
          `requested output field in order: ${outputFields.join(", ")}.`,
      });
    }
    if (
      sourceRange.startRow !== targetRange.startRow ||
      sourceRange.endRow !== targetRange.endRow
    ) {
      return JSON.stringify({
        ok: false,
        error: "sourceAddress and targetAddress must cover the same worksheet rows.",
      });
    }

    if (this.workload) {
      const sameWorkload =
        this.workload.sheetName.toLowerCase() === sheetName.toLowerCase() &&
        this.workload.sourceAddress.toUpperCase() ===
          sourceAddress.toUpperCase() &&
        this.workload.targetAddress.toUpperCase() ===
          targetAddress.toUpperCase() &&
        sameFieldOrder(this.workload.outputFields, outputFields);
      if (sameWorkload) return JSON.stringify(this.buildSummary());
      return JSON.stringify({
        ok: false,
        error:
          `Workload ${this.workload.id} is already registered for this run. ` +
          "Finish that worksheet lookup instead of registering another.",
      });
    }

    let rows: LookupWorkloadRow[] = [];
    const sourceColumn = columnName(sourceRange.startColumn);
    for (
      let pageStart = sourceRange.startRow;
      pageStart <= sourceRange.endRow;
      pageStart += SOURCE_READ_PAGE_ROWS
    ) {
      const pageEnd = Math.min(
        sourceRange.endRow,
        pageStart + SOURCE_READ_PAGE_ROWS - 1,
      );
      const pageAddress = `${sourceColumn}${pageStart}:${sourceColumn}${pageEnd}`;
      const readResult = await readRangeContent(
        sheetName,
        pageAddress,
        { includeFormulas: false, includeFormats: false },
        this.ctx.session.pinnedWorkbookName,
      );
      if (readResult.truncated) {
        return JSON.stringify({
          ok: false,
          error: `Source read ${sheetName}!${pageAddress} was unexpectedly truncated.`,
        });
      }
      const range = readResult.range;
      if (!range) continue;
      for (let index = 0; index < range.values.length; index += 1) {
        const rowNumber = range.startRow + index + 1;
        if (
          rowNumber < sourceRange.startRow ||
          rowNumber > sourceRange.endRow
        ) {
          continue;
        }
        const rawValue = range.values[index]?.[0];
        const searchText =
          rawValue === null || rawValue === undefined
            ? ""
            : String(rawValue).trim();
        if (!searchText) continue;
        rows.push({
          rowNumber,
          sourceCell: `${sourceColumn}${rowNumber}`,
          targetAddress: rowRangeAddress(targetRange, rowNumber, rowNumber),
          searchText,
          lookupKey: normalizeLookupKey(searchText),
          status: "pending",
        });
        if (rows.length > ID_LOOKUP_MAX_NONBLANK_ROWS) {
          return JSON.stringify({
            ok: false,
            error:
              `Worksheet company lookup supports at most ${ID_LOOKUP_MAX_NONBLANK_ROWS} ` +
              `nonblank rows; row ${rowNumber} is nonblank row ` +
              `${ID_LOOKUP_MAX_NONBLANK_ROWS + 1}.`,
            maxNonblankRows: ID_LOOKUP_MAX_NONBLANK_ROWS,
          });
        }
      }
    }

    const occupiedTargets = await this.readOccupiedTargetRows(
      sheetName,
      targetRange,
    );
    if (occupiedTargets.error) {
      return JSON.stringify({ ok: false, error: occupiedTargets.error });
    }
    const skippedPrefilledRows = rows.filter((row) =>
      occupiedTargets.rowNumbers.has(row.rowNumber),
    ).length;
    if (skippedPrefilledRows > 0) {
      // A rerun over a partly filled column is the normal recovery path, so an
      // occupied target cell excludes its row. Overwriting one would discard
      // whatever the workbook already holds there, including values other
      // formulas read.
      rows = rows.filter(
        (row) => !occupiedTargets.rowNumbers.has(row.rowNumber),
      );
    }
    if (rows.length === 0 && skippedPrefilledRows > 0) {
      return JSON.stringify({
        ok: false,
        error:
          `Every target cell in ${sheetName}!${targetAddress} already holds a value, ` +
          "so this range is not an unfinished workload. Target an empty output range instead. " +
          "Do not clear a populated range to make room for the answers.",
        skippedPrefilledRows,
      });
    }

    const remainingIterations = this.ctx.getRemainingExecutorIterations?.();
    const requiredIterations = requiredExecutorIterationsForIdLookup(
      rows.length,
    );
    if (
      typeof remainingIterations === "number" &&
      Number.isFinite(remainingIterations) &&
      remainingIterations < requiredIterations
    ) {
      return JSON.stringify({
        ok: false,
        error:
          `This workload needs ${requiredIterations} executor iteration(s) from ` +
          `registration through terminal headroom, but only ${remainingIterations} remain. ` +
          "Restart the request so the complete worksheet workload can be registered earlier.",
        requiredIterations,
        remainingIterations,
      });
    }

    if (this.ctx.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    this.attachAbortListener();
    this.workloadSequence += 1;
    this.workload = {
      id: `id_lookup_workload_${this.workloadSequence}`,
      stepId,
      sheetName,
      sourceAddress,
      targetAddress,
      sourceRange,
      targetRange,
      outputFields,
      rows,
      activeWaveRows: [],
      lookupCache: new Map(),
      resolutionByLookupKey: new Map(),
    };
    const legacyOutputScope: IdLookupOutputScope = {
      workloadId: this.workload.id,
      ownerStepId: stepId,
      sheetName,
      targetAddress,
    };
    this.ctx.worksheetWorkloadOutputScope = {
      ...legacyOutputScope,
      workloadLabel: "ID lookup workload",
    };
    this.ctx.idLookupOutputScope = legacyOutputScope;
    this.releaseOutputScopeIfComplete();
    this.version += 1;
    return JSON.stringify({
      ...this.buildSummary(),
      ...(skippedPrefilledRows > 0 ? { skippedPrefilledRows } : {}),
      nextAction:
        rows.length > 0
          ? `Call ${NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME}.`
          : "No nonblank source rows require lookup.",
    });
  }

  /**
   * Target rows that already hold a value or formula. A row counts as occupied
   * when any of its target columns is written, because writing part of a row
   * would leave one company described by two different lookups.
   */
  private async readOccupiedTargetRows(
    sheetName: string,
    targetRange: ParsedA1Range,
  ): Promise<{ rowNumbers: Set<number>; error?: string }> {
    const rowNumbers = new Set<number>();
    for (
      let pageStart = targetRange.startRow;
      pageStart <= targetRange.endRow;
      pageStart += SOURCE_READ_PAGE_ROWS
    ) {
      const pageEnd = Math.min(
        targetRange.endRow,
        pageStart + SOURCE_READ_PAGE_ROWS - 1,
      );
      const pageAddress = rowRangeAddress(targetRange, pageStart, pageEnd);
      const readResult = await readRangeContent(
        sheetName,
        pageAddress,
        { includeFormulas: true, includeFormats: false },
        this.ctx.session.pinnedWorkbookName,
      );
      if (readResult.truncated) {
        return {
          rowNumbers,
          error: `Target read ${sheetName}!${pageAddress} was unexpectedly truncated.`,
        };
      }
      const range = readResult.range;
      if (!range) continue;
      const rowCount = Math.max(
        range.values?.length ?? 0,
        range.formulas?.length ?? 0,
      );
      for (let index = 0; index < rowCount; index += 1) {
        const rowNumber = range.startRow + index + 1;
        if (rowNumber < targetRange.startRow || rowNumber > targetRange.endRow) {
          continue;
        }
        const values = range.values?.[index] ?? [];
        const formulas = range.formulas?.[index] ?? [];
        const written = [...values, ...formulas].some(
          (cell) =>
            cell !== null && cell !== undefined && String(cell).trim() !== "",
        );
        if (written) rowNumbers.add(rowNumber);
      }
    }
    return { rowNumbers };
  }

  async lookupNextWave(
    rawInput: unknown,
    signal?: AbortSignal,
  ): Promise<ToolCallDetailedResult> {
    const workload = this.workload;
    const record = isRecord(rawInput) ? rawInput : {};
    const workloadId = readTrimmedString(record, "workloadId");
    const workloadError = this.validateWorkloadId(workloadId);
    if (workloadError || !workload) {
      return { output: JSON.stringify({ ok: false, error: workloadError }) };
    }
    if (workload.activeWaveRows.length > 0) {
      return {
        output: JSON.stringify({
          ok: false,
          error:
            `Rows ${workload.activeWaveRows.join(", ")} are still active. ` +
            `Call ${COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME} before requesting another wave.`,
        }),
      };
    }

    const waveRows = workload.rows
      .filter((row) => row.status === "pending")
      .slice(0, ID_LOOKUP_MAX_CALLS_PER_WAVE);
    if (waveRows.length === 0) {
      return { output: JSON.stringify(this.buildSummary()) };
    }

    const firstRowByKey = new Map<string, LookupWorkloadRow>();
    for (const row of waveRows) {
      if (!firstRowByKey.has(row.lookupKey)) {
        firstRowByKey.set(row.lookupKey, row);
      }
    }
    const uniqueEntries = Array.from(firstRowByKey.entries());
    const freshEntries = uniqueEntries.filter(
      ([lookupKey]) => !workload.lookupCache.has(lookupKey),
    );
    let completed: Array<{
      lookupKey: string;
      row: LookupWorkloadRow;
      args: {
        readonly search_text: string;
        readonly filters: null;
        readonly limit: number;
      };
      result: ToolCallDetailedResult;
    }>;
    try {
      completed = await mapWithConcurrencyLimit(
        freshEntries,
        LOOKUP_CONCURRENCY,
        async ([lookupKey, row]) => {
        throwIfAborted(signal);
        const args = {
          search_text: row.searchText,
          filters: null,
          limit: FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
        } as const;
        let result: ToolCallDetailedResult;
        try {
          result = await executeIdLookupToolDetailed(args, signal);
        } catch (error) {
          throwIfAborted(signal);
          const message =
            error instanceof Error ? error.message : String(error);
          return {
            lookupKey,
            row,
            args,
            result: {
              output: JSON.stringify({
                success: false,
                status: "error",
                search_text: row.searchText,
                candidate_count: 0,
                candidates: [],
                fallback_reason: "id_lookup_failed",
                error_code: null,
                error: message,
              }),
              observationMetadata: {
                lookupPath: "direct_factset",
                candidateCount: 0,
                fallbackRecommended: true,
                fallbackReason: "id_lookup_failed",
              },
            },
          };
        }
        const errorCode = result.observationMetadata?.errorCode;
        if (
          typeof errorCode === "string" &&
          REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(errorCode as never)
        ) {
          throw new IdLookupReauthenticationRequiredError(errorCode);
        }
        return { lookupKey, row, args, result };
        },
      );
    } catch (error) {
      if (!(error instanceof IdLookupReauthenticationRequiredError)) {
        throw error;
      }
      const remainingRows = workload.rows.filter(
        (row) => row.status !== "verified",
      ).length;
      this.reset();
      return {
        output: JSON.stringify({
          ok: false,
          errorCode: error.errorCode,
          reauthenticationRequired: true,
          remainingRows,
          error:
            "FactSet authentication expired before this wave was committed. No rows from the failed wave were written.",
          nextAction:
            "Reconnect FactSet, then start a new ID lookup workload against the same source and target ranges. Existing target rows will be skipped.",
        }),
        observationMetadata: {
          lookupPath: "worksheet_workload",
          fallbackRecommended: true,
          fallbackReason: error.errorCode,
          errorCode: error.errorCode,
        },
      };
    }
    throwIfAborted(signal);

    for (const { lookupKey, args, result } of completed) {
      const storedResult = parseStoredLookupResult(
        result.output,
        result.observationMetadata,
      );
      workload.lookupCache.set(lookupKey, storedResult);
      this.routingRecords.push({
        args,
        output: result.output,
        rowNumbers: workload.rows
          .filter((row) => row.lookupKey === lookupKey)
          .map((row) => row.rowNumber),
      });
    }

    workload.activeWaveRows = waveRows.map((row) => row.rowNumber);
    for (const row of waveRows) row.status = "looked_up";
    this.version += 1;

    const uniqueResults = uniqueEntries.map(([lookupKey]) => {
      const result = workload.lookupCache.get(lookupKey);
      if (!result) {
        throw new Error(`Missing cached lookup result for ${lookupKey}.`);
      }
      return result;
    });
    const requestIds = Array.from(
      new Set(
        uniqueResults
          .map((result) => result.observationMetadata?.lookupRequestId)
          .filter(
            (value): value is string =>
              typeof value === "string" && value.length > 0,
          ),
      ),
    ).slice(0, MAX_CHILD_OBSERVATIONS);
    const childObservations = completed
      .slice(0, MAX_CHILD_OBSERVATIONS)
      .map(({ lookupKey, result }) => {
        const rowNumbers = workload.rows
          .filter((row) => row.lookupKey === lookupKey)
          .map((row) => row.rowNumber);
        return {
          ...sanitizeChildObservation(result.observationMetadata),
          rowCount: rowNumbers.length,
          rowNumbers: rowNumbers.slice(0, MAX_CHILD_ROW_NUMBERS),
        };
      });
    const reusedRowCount = waveRows.length - freshEntries.length;
    const candidateCount = uniqueResults.reduce(
      (sum, result) => sum + result.candidates.length,
      0,
    );

    return {
      output: JSON.stringify({
        ok: true,
        workloadId: workload.id,
        outputFields: [...workload.outputFields],
        waveSize: waveRows.length,
        uniqueLookupCount: uniqueEntries.length,
        executedLookupCount: freshEntries.length,
        reusedRowCount,
        remainingRows: workload.rows.filter(
          (row) => row.status !== "verified",
        ).length,
        rows: waveRows.map((row) => {
          const result = workload.lookupCache.get(row.lookupKey);
          return {
            rowNumber: row.rowNumber,
            sourceCell: row.sourceCell,
            targetAddress: row.targetAddress,
            searchText: row.searchText,
            lookup: result ? modelLookupView(result) : null,
          };
        }),
        nextAction:
          `For every row, select one zero-based candidateIndex or one closed ` +
          `unresolvedReason, then call ${COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME}.`,
      }),
      observationMetadata: {
        lookupPath: "worksheet_workload",
        workloadId: workload.id,
        rowCount: waveRows.length,
        uniqueLookupCount: uniqueEntries.length,
        executedLookupCount: freshEntries.length,
        reusedRowCount,
        candidateCount,
        lookupRequestIds: requestIds,
        childObservations,
      },
    };
  }

  async commitWave(rawInput: unknown): Promise<string> {
    const workload = this.workload;
    const record = isRecord(rawInput) ? rawInput : {};
    const workloadId = readTrimmedString(record, "workloadId");
    const workloadError = this.validateWorkloadId(workloadId);
    if (workloadError || !workload) {
      return JSON.stringify({ ok: false, error: workloadError });
    }
    if (workload.activeWaveRows.length === 0) {
      return JSON.stringify({
        ok: false,
        error: `No active wave exists. Call ${NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME} first.`,
      });
    }

    const parsed = parseCommitDecisions(record.resolutions);
    if (!parsed.decisions) {
      return JSON.stringify({ ok: false, error: parsed.error });
    }
    const decisions = [...parsed.decisions].sort(
      (left, right) => left.rowNumber - right.rowNumber,
    );
    const activeRows = [...workload.activeWaveRows].sort(
      (left, right) => left - right,
    );
    const suppliedRows = decisions.map((decision) => decision.rowNumber);
    if (
      suppliedRows.length !== activeRows.length ||
      new Set(suppliedRows).size !== suppliedRows.length ||
      suppliedRows.some((rowNumber, index) => rowNumber !== activeRows[index])
    ) {
      return JSON.stringify({
        ok: false,
        error:
          `resolutions must account for active rows exactly once. Expected rows: ` +
          `${activeRows.join(", ")}; received: ${suppliedRows.join(", ")}.`,
      });
    }

    const validated: ValidatedCommitResolution[] = [];
    const waveResolutionByKey = new Map<string, StoredResolution>();
    for (const decision of decisions) {
      const row = workload.rows.find(
        (candidate) => candidate.rowNumber === decision.rowNumber,
      );
      if (!row) {
        return JSON.stringify({
          ok: false,
          error: `Unknown active worksheet row ${decision.rowNumber}.`,
        });
      }
      const lookupResult = workload.lookupCache.get(row.lookupKey);
      if (!lookupResult) {
        return JSON.stringify({
          ok: false,
          error: `No stored lookup result exists for row ${decision.rowNumber}.`,
        });
      }
      const resolution: StoredResolution = {
        candidateIndex: decision.candidateIndex,
        unresolvedReason: decision.unresolvedReason,
      };
      const priorResolution =
        workload.resolutionByLookupKey.get(row.lookupKey) ??
        waveResolutionByKey.get(row.lookupKey);
      if (
        priorResolution &&
        !sameStoredResolution(priorResolution, resolution)
      ) {
        return JSON.stringify({
          ok: false,
          error:
            `Rows sharing normalized source text must use the same resolution. ` +
            `Row ${decision.rowNumber} contradicts the earlier resolution for "${row.searchText}".`,
        });
      }
      waveResolutionByKey.set(row.lookupKey, resolution);

      const derived = deriveResolutionValues(
        decision,
        lookupResult,
        workload.outputFields,
      );
      if (!derived.values) {
        return JSON.stringify({ ok: false, error: derived.error });
      }
      validated.push({
        ...decision,
        lookupKey: row.lookupKey,
        values: derived.values,
      });
    }

    const groups = groupConsecutiveResolutions(validated);
    const modifications = {
      worksheets: [
        {
          sheetName: workload.sheetName,
          ranges: groups.map((group) => ({
            address: rowRangeAddress(
              workload.targetRange,
              group[0].rowNumber,
              group[group.length - 1].rowNumber,
            ),
            action: "fill" as const,
            values: group.map((resolution) => resolution.values),
          })),
        },
      ],
      message:
        `Committed FactSet company lookup rows ${activeRows[0]}-${activeRows.at(-1)}`,
    };
    const applyOutput = await executeModifyWorkbookTool(this.ctx, {
      stepId: workload.stepId,
      targetSheet: workload.sheetName,
      modifications,
    });
    const applyResult = parseModelOutput(applyOutput);
    if (
      !isRecord(applyResult) ||
      applyResult.ok !== true ||
      applyResult.applied !== true
    ) {
      return JSON.stringify({
        ok: false,
        error: "The lookup wave write was not applied.",
        applyResult,
      });
    }

    const validatedByRow = new Map(
      validated.map((resolution) => [resolution.rowNumber, resolution]),
    );
    let madeCommitProgress = false;
    for (const rowNumber of activeRows) {
      const row = workload.rows.find(
        (candidate) => candidate.rowNumber === rowNumber,
      );
      const resolution = validatedByRow.get(rowNumber);
      if (!row || !resolution) continue;
      row.candidateIndex = resolution.candidateIndex ?? undefined;
      row.unresolvedReason = resolution.unresolvedReason ?? undefined;
      workload.resolutionByLookupKey.set(row.lookupKey, {
        candidateIndex: resolution.candidateIndex,
        unresolvedReason: resolution.unresolvedReason,
      });
      if (row.status !== "committed") {
        row.status = "committed";
        madeCommitProgress = true;
      }
    }
    if (madeCommitProgress) this.version += 1;

    const verificationErrors: string[] = [];
    for (const group of groups) {
      const address = rowRangeAddress(
        workload.targetRange,
        group[0].rowNumber,
        group[group.length - 1].rowNumber,
      );
      const readResult = await readRangeContent(
        workload.sheetName,
        address,
        { includeFormulas: false, includeFormats: false },
        this.ctx.session.pinnedWorkbookName,
      );
      if (!readResult.range || readResult.truncated) {
        verificationErrors.push(
          `${workload.sheetName}!${address} could not be read back completely.`,
        );
        continue;
      }
      const range = readResult.range;
      for (const resolution of group) {
        const rowIndex = resolution.rowNumber - (range.startRow + 1);
        const columnIndex =
          workload.targetRange.startColumn - (range.startColumn + 1);
        const actual = range.values[rowIndex]?.slice(
          columnIndex,
          columnIndex + workload.outputFields.length,
        );
        if (
          !actual ||
          actual.length !== workload.outputFields.length ||
          actual.some(
            (value, index) =>
              String(value ?? "") !== resolution.values[index],
          )
        ) {
          verificationErrors.push(
            `${workload.sheetName}!${rowRangeAddress(
              workload.targetRange,
              resolution.rowNumber,
              resolution.rowNumber,
            )} did not match the committed values.`,
          );
        }
      }
    }
    if (verificationErrors.length > 0) {
      return JSON.stringify({
        ok: false,
        applied: true,
        error: "Lookup wave read-back verification failed.",
        verificationErrors,
        retryWith: COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
      });
    }

    for (const rowNumber of activeRows) {
      const row = workload.rows.find(
        (candidate) => candidate.rowNumber === rowNumber,
      );
      const resolution = validatedByRow.get(rowNumber);
      if (!row || !resolution) continue;
      row.status = "verified";
    }
    workload.activeWaveRows = [];
    this.version += 1;
    this.releaseOutputScopeIfComplete();

    return JSON.stringify({
      ...this.buildSummary(),
      committedRows: activeRows,
      nextAction: this.isComplete
        ? "All registered source rows are written and verified. Complete the run step and return the final response."
        : `Call ${NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME} for the next pending rows.`,
    });
  }

  private releaseOutputScopeIfComplete(): void {
    const workload = this.workload;
    if (!workload || !this.isComplete) return;
    this.releaseOutputScope(workload.id);
    this.detachAbortListener();
  }

  private releaseOutputScope(workloadId: string): void {
    if (
      this.ctx.worksheetWorkloadOutputScope?.workloadId === workloadId
    ) {
      delete this.ctx.worksheetWorkloadOutputScope;
    }
    if (this.ctx.idLookupOutputScope?.workloadId === workloadId) {
      delete this.ctx.idLookupOutputScope;
    }
  }

  private attachAbortListener(): void {
    if (
      this.abortListenerAttached ||
      !this.ctx.signal ||
      this.ctx.signal.aborted
    ) {
      return;
    }
    this.ctx.signal.addEventListener("abort", this.handleRuntimeAbort, {
      once: true,
    });
    this.abortListenerAttached = true;
  }

  private detachAbortListener(): void {
    if (!this.abortListenerAttached || !this.ctx.signal) return;
    this.ctx.signal.removeEventListener("abort", this.handleRuntimeAbort);
    this.abortListenerAttached = false;
  }

  private validateWorkloadId(workloadId: string): string | null {
    if (!this.workload) {
      return `No workload is registered. Call ${START_ID_LOOKUP_WORKLOAD_TOOL_NAME} first.`;
    }
    if (!workloadId || workloadId !== this.workload.id) {
      return `Unknown workloadId "${workloadId}". Active workload: ${this.workload.id}.`;
    }
    return null;
  }

  private buildSummary(): Record<string, unknown> {
    const workload = this.workload;
    if (!workload) {
      return { ok: false, error: "No lookup workload is registered." };
    }
    const pendingRows = workload.rows.filter(
      (row) => row.status === "pending",
    ).length;
    const lookedUpRows = workload.rows.filter(
      (row) => row.status === "looked_up",
    ).length;
    const committedRows = workload.rows.filter(
      (row) => row.status === "committed",
    ).length;
    const verifiedRows = workload.rows.filter(
      (row) => row.status === "verified",
    ).length;
    const unresolvedRows = workload.rows.filter(
      (row) => row.unresolvedReason !== undefined,
    ).length;
    return {
      ok: true,
      workloadId: workload.id,
      sheetName: workload.sheetName,
      sourceAddress: workload.sourceAddress,
      targetAddress: workload.targetAddress,
      outputFields: [...workload.outputFields],
      totalRows: workload.rows.length,
      uniqueLookupKeys: new Set(workload.rows.map((row) => row.lookupKey)).size,
      pendingRows,
      lookedUpRows,
      committedRows,
      verifiedRows,
      unresolvedRows,
      remainingRows: workload.rows.length - verifiedRows,
      activeWaveRows: [...workload.activeWaveRows],
      complete: this.isComplete,
    };
  }
}
